/**
 * Ficha de producto: variantes, barra fija de compra y guía de tallas.
 *
 * La selección de variante se resuelve contra los datos serializados en la
 * página, sin ir al servidor: cambiar de talla debe ser instantáneo. La compra
 * sí va por fetch a /cart/add.js para no recargar.
 */

class ScProduct extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.variantInput = this.querySelector('[data-variant-id]');
    this.priceEl = this.querySelector('[data-price]');
    this.submit = this.querySelector('[data-submit]');
    this.submitText = this.querySelector('[data-submit-text]');
    this.status = this.querySelector('[data-status]');
    this.stock = this.querySelector('[data-stock]');
    this.bar = this.querySelector('[data-bar]');
    this.barPrice = this.querySelector('[data-bar-price]');
    this.guide = this.querySelector('[data-guide]');
    this.radios = [...this.querySelectorAll('.sc-product__radio')];

    this.lowThreshold = Number(this.dataset.lowThreshold || 0);
    this.variants = this.readVariants();

    this.form?.addEventListener('submit', (event) => this.onSubmit(event));

    if (this.variants.length > 0) {
      this.radios.forEach((radio) =>
        radio.addEventListener('change', () => this.onOptionChange())
      );
      this.updateAvailability();
    }

    this.setupStickyBar();
    this.setupGuide();
  }

  readVariants() {
    const node = this.querySelector('[data-variants]');
    if (!node) return [];
    try {
      return JSON.parse(node.textContent);
    } catch {
      return [];
    }
  }

  /**
   * Cuántas quedan de una variante después de llevarse `llevadas`.
   *
   * Devuelve null cuando la prenda no lleva control de existencias, que es lo
   * correcto: sin control, cualquier número sería inventado. El aviso de la
   * cesta entiende el null como «no enseñes nada».
   */
  quedanDe(varianteId, llevadas = 1) {
    const v = this.variants.find((x) => x.id === varianteId);
    if (!v || !v.tracked) return null;
    return Math.max(0, Number(v.inventory || 0) - Number(llevadas || 0));
  }

  get selectedOptions() {
    const values = [];
    this.radios
      .filter((r) => r.checked)
      .forEach((r) => {
        values[Number(r.dataset.optionPosition) - 1] = r.value;
      });
    return values;
  }

  findVariant(options) {
    return this.variants.find(
      (v) =>
        v.options.length === options.length &&
        v.options.every((opt, i) => opt === options[i])
    );
  }

  onOptionChange() {
    const variant = this.findVariant(this.selectedOptions);
    this.updateAvailability();

    if (!variant) {
      this.setSubmit({ enabled: false, label: 'No disponible' });
      this.toggleStock(null);
      return;
    }

    if (this.variantInput) this.variantInput.value = variant.id;

    if (this.priceEl) {
      // El precio de referencia (sc-ref-currency.js) se recalcula al ver cambiar este atributo.
      this.priceEl.dataset.scPriceCents = variant.priceCents;
      this.priceEl.innerHTML = variant.onSale
        ? `<s class="sc-product__was">${variant.compareAtPrice}</s>${variant.price}`
        : variant.price;
    }
    if (this.barPrice) this.barPrice.textContent = variant.price;

    this.setSubmit({
      enabled: variant.available,
      label: variant.available ? 'Añadir a la cesta' : 'Agotado',
    });

    this.toggleStock(variant);
    this.scrollToVariantMedia(variant);

    // URL compartible y botón atrás coherente, sin recargar.
    const url = new URL(window.location);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
  }

  /**
   * Escasez sólo cuando es real: inventario gestionado y por debajo del umbral.
   * Una cifra inventada destruye la confianza que sostiene el ticket alto.
   */
  toggleStock(variant) {
    if (!this.stock) return;

    const show =
      variant &&
      variant.tracked &&
      variant.available &&
      variant.inventory > 0 &&
      variant.inventory <= this.lowThreshold;

    this.stock.hidden = !show;
    if (show) this.stock.textContent = `Últimas ${variant.inventory} piezas`;
  }

  /** Lleva la galería a la foto de la variante elegida, si la tiene asignada. */
  scrollToVariantMedia(variant) {
    if (!variant.media) return;
    const target = this.querySelector(`[data-media-position="${variant.media}"]`);
    target?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }

  updateAvailability() {
    const selected = this.selectedOptions;

    this.radios.forEach((radio) => {
      const position = Number(radio.dataset.optionPosition) - 1;
      const candidate = [...selected];
      candidate[position] = radio.value;

      const reachable = this.variants.some(
        (v) =>
          v.available &&
          v.options[position] === radio.value &&
          v.options.every((opt, i) => i === position || !candidate[i] || opt === candidate[i])
      );

      radio.disabled = !reachable;
    });
  }

  setSubmit({ enabled, label }) {
    if (this.submit) this.submit.disabled = !enabled;
    if (this.submitText) this.submitText.textContent = label;
  }

  /**
   * La barra aparece cuando el botón principal sale de vista. Se observa el
   * botón, no un scroll arbitrario: así funciona igual en cualquier viewport.
   */
  setupStickyBar() {
    if (!this.bar || !this.submit) return;

    this.bar.hidden = false;

    const observer = new IntersectionObserver(
      ([entry]) => {
        this.bar.classList.toggle('is-visible', !entry.isIntersecting);
      },
      { rootMargin: '0px 0px -80px 0px' }
    );

    observer.observe(this.submit);
  }

  setupGuide() {
    if (!this.guide) return;

    this.querySelectorAll('[data-open-guide]').forEach((btn) => {
      btn.addEventListener('click', () => this.guide.showModal());
    });

    this.querySelector('[data-close-guide]')?.addEventListener('click', () =>
      this.guide.close()
    );

    // Clic en el backdrop cierra: el <dialog> nativo no lo hace solo.
    this.guide.addEventListener('click', (event) => {
      if (event.target === this.guide) this.guide.close();
    });
  }

  async onSubmit(event) {
    event.preventDefault();
    if (!this.form || this.submit?.disabled) return;

    this.setStatus('Añadiendo…');
    this.submit.disabled = true;

    try {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(this.form),
      });

      const data = await res.json();

      if (!res.ok) {
        // Shopify explica el motivo en `description` (p. ej. stock insuficiente).
        this.setStatus(data.description || 'No se ha podido añadir.');
        this.submit.disabled = false;
        return;
      }

      this.setStatus('Añadido a la cesta');
      // Cuántas quedan va aquí porque es el único sitio que lo sabe: el
      // inventario real llega en Liquid, y la API de carrito de Shopify no lo
      // devuelve. Se descuenta lo que se acaba de llevar. Si la prenda no
      // lleva control de existencias no va nada, y el aviso no enseña número.
      const quedan = this.quedanDe(data.variant_id, data.quantity);
      document.dispatchEvent(new CustomEvent('cart:update', { detail: { item: data, quedan } }));
      this.submit.disabled = false;
    } catch {
      this.setStatus('Error de conexión. Inténtalo de nuevo.');
      this.submit.disabled = false;
    }
  }

  setStatus(message) {
    if (this.status) this.status.textContent = message;
  }
}

if (!customElements.get('sc-product')) {
  customElements.define('sc-product', ScProduct);
}
